/**
 * Vintage Tag — pill marker for status, category, and ledger classification.
 *
 * Tones map to the design's --tint-{tone} pairs. Three emphases:
 *   soft    — tinted bg + tone text (default; the quiet, scannable marker)
 *   solid   — filled tone bg + contrast text (loud; counts, live status)
 *   outline — transparent + tone hairline + tone text (on busy surfaces)
 *
 * Pure visual component — renders a `<span>` (no Mantine functional layer).
 * Distinct from Mantine's `<Badge>` (re-exported from the barrel) which keeps
 * the legacy filled/light/outline + onClick + gradient API.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './Badge.module.css';

export type TagTone =
  | 'brand'
  | 'gold'
  | 'green'
  | 'neutral'
  | 'warning'
  | 'error'
  | 'info';

export type TagVariant = 'soft' | 'solid' | 'outline';

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  /** Tone — picks the design's --tint-{tone} pair. Default `neutral`. */
  readonly tone?: TagTone;
  /** Emphasis. Default `soft`. */
  readonly variant?: TagVariant;
  /** Show a 6px round indicator on the left in the tag's text colour. */
  readonly withDot?: boolean;
  /** Size — `sm` is the dense table / chip preset; `md` is the default. */
  readonly size?: 'sm' | 'md';
  readonly children?: ReactNode;
}

const TONE_CLASS: Record<TagTone, string> = {
  brand: styles.brand ?? '',
  gold: styles.gold ?? '',
  green: styles.green ?? '',
  neutral: styles.neutral ?? '',
  warning: styles.warning ?? '',
  error: styles.error ?? '',
  info: styles.info ?? '',
};

const VARIANT_CLASS: Record<TagVariant, string | undefined> = {
  soft: undefined,
  solid: styles.solid,
  outline: styles.outline,
};

export const Tag = forwardRef<HTMLSpanElement, TagProps>(function Tag(
  {
    tone = 'neutral',
    variant = 'soft',
    withDot = false,
    size = 'md',
    className,
    children,
    ...rest
  },
  ref,
) {
  const classes = [
    styles.badge,
    TONE_CLASS[tone],
    VARIANT_CLASS[variant],
    size === 'sm' ? styles.sm : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span ref={ref} className={classes} {...rest}>
      {withDot ? <span className={styles.dot} aria-hidden /> : null}
      {children}
    </span>
  );
});
